import { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ConfirmDialog } from '../../../components/Modal/ConfirmDialog';
import { DateValue, useFormatDate } from '../../../hooks/useFormatDate';
import { T, useT } from '../../../translation/src';
import { useOrganisationCancelSubscriptionMutation } from '../../../types/graphqlTypes';

const useTranslations = () => {
  const cancelled = useT('Your subscription was cancelled. It stays active until the end of the term.');
  const failed = useT('Cancelling failed. Please try again.');
  return { cancelled, failed };
};

export interface CancelSubscriptionModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  endDate: DateValue;
}

// Cancelling stops the renewal: the term that was paid for runs to its end,
// then the organisation is on Flex. The mutation returns the organisation, so
// the subscription header picks up the scheduled change from the cache.
const CancelSubscriptionModal_: React.FC2<CancelSubscriptionModalProps> = ({ onClose, open, organisationId, endDate }) => {
  const [cancelSubscription, { loading }] = useOrganisationCancelSubscriptionMutation();
  const translations = useTranslations();
  const { formatDay } = useFormatDate();

  const onConfirm = useCallback(async () => {
    try {
      await cancelSubscription({ variables: { organisationId } });
      toast.success(translations.cancelled);
    } catch (error) {
      console.error(error);
      toast.error(translations.failed);
    }
  }, [cancelSubscription, organisationId, translations.cancelled, translations.failed]);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      loading={loading}
      title={<T _str="cancel subscription" swc />}
      message={<T _str="Your subscription will not renew. You keep your plan and monthly projects until {date}. After that you buy projects as you need them." args={{ date: formatDay(endDate) }} />}
      confirmLabel={<T _str="cancel subscription" swc />}
      cancelLabel={<T _str="keep subscription" swc />}
    />
  );
};

export const CancelSubscriptionModal = memo(CancelSubscriptionModal_);
